import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * The bank contract.
 *
 * The one law, restated where the wire can see it: bank lines are facts and
 * import wholesale; matching is a judgement, so a match crosses this API only
 * as a PROPOSAL with its evidence, and becomes real only through a person's
 * confirm. There is no shape here by which the server announces "I matched
 * these for you."
 */
export const FINANCIAL_ACCOUNT_KINDS = [
    'chequing',
    'savings',
    'credit_card',
    'line_of_credit',
    'cash',
    'loan',
    'merchant',
];
export const financialAccountKindSchema = z.enum(FINANCIAL_ACCOUNT_KINDS);
export const FINANCIAL_ACCOUNT_KIND_LABELS = {
    chequing: 'Chequing',
    savings: 'Savings',
    credit_card: 'Credit card',
    line_of_credit: 'Line of credit',
    cash: 'Cash',
    loan: 'Loan',
    merchant: 'Payment processor',
};
export const financialAccountSchema = z.object({
    id: uuidSchema,
    kind: financialAccountKindSchema,
    name: z.string(),
    institutionName: z.string().nullable(),
    /** The last four digits, which is how people recognise their own cards. */
    accountMask: z.string().nullable(),
    currencyCode: z.string(),
    isReconcilable: z.boolean(),
});
export const createAccountRequestSchema = z.strictObject({
    kind: financialAccountKindSchema,
    name: z.string().trim().min(1).max(120),
    institutionName: z.string().trim().max(120).nullable().optional(),
    accountMask: z
        .string()
        .regex(/^[0-9]{4}$/, 'The last four digits only.')
        .nullable()
        .optional(),
});
/* -------------------------------------------------------------------------- */
/* Statements waiting to be imported                                          */
/* -------------------------------------------------------------------------- */
export const STATEMENT_READING_STATUSES = ['balanced', 'unbalanced', 'unreadable'];
export const statementReadingStatusSchema = z.enum(STATEMENT_READING_STATUSES);
/**
 * What the model read off a PDF or photographed statement, and whether it
 * proved. Absent on a CSV or OFX, which the server parses on request.
 */
export const statementReadingSummarySchema = z.object({
    status: statementReadingStatusSchema,
    attempt: z.number().int(),
    accountKind: z.string().nullable(),
    accountMask: z.string().nullable(),
    failure: z.string().nullable(),
});
export const pendingStatementSchema = z.object({
    documentId: uuidSchema,
    filename: z.string().nullable(),
    detectedKind: z.string(),
    receivedAt: isoDateTimeSchema,
    reading: statementReadingSummarySchema.nullable().optional(),
});
/**
 * Opening balance plus every line equals the closing balance, or it names
 * what fails. Minor units as strings, like every amount on the wire.
 */
export const statementProofSchema = z.object({
    balanced: z.boolean(),
    openingMinor: z.string().nullable(),
    closingMinor: z.string().nullable(),
    computedClosingMinor: z.string().nullable(),
    differenceMinor: z.string().nullable(),
    pagesChecked: z.number().int(),
    failure: z.string().nullable(),
});
/** What the server's parser found, shown BEFORE anything is committed. */
export const statementPreviewSchema = z.object({
    documentId: uuidSchema,
    filename: z.string().nullable(),
    sourceKind: z.enum(['bank_csv', 'bank_ofx', 'bank_pdf']),
    rowCount: z.number().int(),
    skippedCount: z.number().int(),
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    /** The proof a read statement passed or failed. Absent on a CSV or OFX. */
    proof: statementProofSchema.nullable().optional(),
    accountKind: z.string().nullable().optional(),
    accountMask: z.string().nullable().optional(),
    /** The account whose last four digits and kind match the statement, if one does. */
    suggestedAccountId: uuidSchema.nullable().optional(),
    /** First rows, so a person can see it parsed sanely before importing. */
    sample: z.array(z.object({
        postedOn: z.string(),
        description: z.string(),
        amountMinor: z.string(),
    })),
});
export const importStatementRequestSchema = z.strictObject({
    financialAccountId: uuidSchema,
});
export const importSummarySchema = z.object({
    accepted: z.number().int(),
    duplicates: z.number().int(),
    suggested: z.number().int(),
});
/* -------------------------------------------------------------------------- */
/* Reconciliation                                                             */
/* -------------------------------------------------------------------------- */
export const BANK_TRANSACTION_STATES = [
    'unmatched',
    'suggested',
    'matched',
    'ignored',
    'duplicate',
    'explained',
];
export const bankTransactionStateSchema = z.enum(BANK_TRANSACTION_STATES);
/** The matcher's proposal, evidence attached. Never self-executing. */
export const matchProposalSchema = z.object({
    transactionId: uuidSchema,
    confidence: z.number(),
    reasoning: z.string().nullable(),
    vendorName: z.string().nullable(),
    transactionDate: z.string(),
    grossAmountMinor: z.string(),
});
export const bankTransactionSchema = z.object({
    id: uuidSchema,
    accountName: z.string(),
    /**
     * The account's kind. On a card or credit line the sign does not say which
     * way money went (exports disagree), so those lines are read as purchases.
     */
    accountKind: financialAccountKindSchema,
    postedOn: z.string(),
    description: z.string(),
    /** The bank's own sign convention: money out is negative. A string, always. */
    amountMinor: z.string(),
    state: bankTransactionStateSchema,
    matchedTransactionId: uuidSchema.nullable(),
    proposal: matchProposalSchema.nullable(),
});
export const listBankTransactionsQuerySchema = z.object({
    state: bankTransactionStateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const confirmMatchRequestSchema = z.strictObject({
    transactionId: uuidSchema,
});
export const dismissMatchRequestSchema = z.strictObject({
    /** "Not this one" versus "not a business transaction at all". */
    outcome: z.enum(['unmatched', 'ignored']),
});
/** What a deposit was. Only a customer payment becomes income. */
export const DEPOSIT_KINDS = [
    'customer_payment',
    'transfer',
    'owner_contribution',
    'loan',
    'supplier_refund',
    'other',
];
export const depositKindSchema = z.enum(DEPOSIT_KINDS);
export const DEPOSIT_KIND_LABELS = {
    customer_payment: 'A customer paid',
    transfer: 'A transfer from another account of ours',
    owner_contribution: 'The owner put money in',
    loan: 'A loan',
    supplier_refund: 'A refund from a supplier',
    other: 'Something else',
};
/** What money leaving the account was, when no record explains it. */
export const PAYMENT_KINDS = [
    'payroll',
    'remittance',
    'transfer',
    'loan_payment',
    'owner_draw',
    'other',
];
export const paymentKindSchema = z.enum(PAYMENT_KINDS);
export const PAYMENT_KIND_LABELS = {
    payroll: 'Net pay to an employee',
    remittance: 'A remittance to the CRA',
    transfer: 'A transfer to another account of ours',
    loan_payment: 'A loan payment',
    owner_draw: 'The owner took money out',
    other: 'Something else',
};
export const explainPaymentRequestSchema = z
    .object({
    as: paymentKindSchema,
    note: z.string().trim().max(500).optional(),
})
    .refine((value) => value.as !== 'other' || (value.note ?? '').trim() !== '', {
    message: '"Something else" needs a word or two.',
    path: ['note'],
});
export const explainDepositRequestSchema = z
    .object({
    as: depositKindSchema,
    note: z.string().trim().max(500).optional(),
})
    .refine((value) => value.as !== 'other' || (value.note ?? '') !== '', {
    message: 'Say what it was.',
    path: ['note'],
});
//# sourceMappingURL=bank.js.map